import React, { useEffect, useRef } from 'react';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { CopilotMessage, CopilotActionProposed } from '../../models/copilot.model';
import CopilotBubble from './CopilotBubble';
import CopilotActionCard from './CopilotActionCard';

interface Props {
  messages: CopilotMessage[];
  streaming: boolean;
  currentToolRunning: string | null;
  pendingAction: CopilotActionProposed | null;
  onConfirm: (actionId: string) => void;
  onReject: (actionId: string) => void;
}

const CopilotMessages: React.FC<Props> = ({messages, streaming, currentToolRunning, pendingAction, onConfirm, onReject}) => {
  const {lang} = StateLanguage()
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = scrollContainerRef.current
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
    }
  }, [messages, currentToolRunning, pendingAction])

  return (
    <div ref={scrollContainerRef} className="card-body scroll-y" style={{minHeight: 0, flex: '1 1 0'}}>
      {/* Empty state */}
      {messages.length === 0 && !streaming && (
        <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted">
          <span className="svg-icon svg-icon-3x mb-4 svg-icon-muted">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path opacity="0.3" d="M20 3H4C2.89543 3 2 3.89543 2 5V16C2 17.1046 2.89543 18 4 18H4.5C5.05228 18 5.5 18.4477 5.5 19V21.5052C5.5 22.1441 6.21232 22.5253 6.74118 22.1708L11.3753 19.0955C11.5301 18.9912 11.7123 18.9354 11.8989 18.9354H20C21.1046 18.9354 22 18.0399 22 16.9354V5C22 3.89543 21.1046 3 20 3Z" fill="currentColor"/>
              <rect x="6" y="12" width="7" height="2" rx="1" fill="currentColor"/>
              <rect x="6" y="7" width="12" height="2" rx="1" fill="currentColor"/>
            </svg>
          </span>
          <p className="fs-6 fw-bold">{lang.labels.copilotPlaceholder}</p>
        </div>
      )}

      {/* Message bubbles */}
      {messages.map((msg) => (
        <CopilotBubble key={msg.id} msg={msg} />
      ))}

      {/* Tool running indicator */}
      {currentToolRunning && (
        <div className="d-flex justify-content-start mb-10">
          <div className="d-flex flex-column align-items-start">
            <div className="p-4 rounded bg-light-info text-gray-700 fs-8">
              <span className="me-2">&#128269;</span>{lang.labels.copilotToolRunning}: {currentToolRunning}...
            </div>
          </div>
        </div>
      )}

      {/* Pending action card */}
      {pendingAction && (
        <div className="d-flex justify-content-start mb-10">
          <div className="d-flex flex-column align-items-start" style={{width: '100%'}}>
            <div className="d-flex align-items-center mb-2">
              <div className="symbol symbol-35px me-3">
                <div className="symbol-label fs-8 fw-bold bg-light-info text-info rounded-circle">
                  <span className="svg-icon svg-icon-3 svg-icon-info">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path opacity="0.3" d="M20 3H4C2.89543 3 2 3.89543 2 5V16C2 17.1046 2.89543 18 4 18H4.5C5.05228 18 5.5 18.4477 5.5 19V21.5052C5.5 22.1441 6.21232 22.5253 6.74118 22.1708L11.3753 19.0955C11.5301 18.9912 11.7123 18.9354 11.8989 18.9354H20C21.1046 18.9354 22 18.0399 22 16.9354V5C22 3.89543 21.1046 3 20 3Z" fill="currentColor"/>
                      <rect x="6" y="12" width="7" height="2" rx="1" fill="currentColor"/>
                      <rect x="6" y="7" width="12" height="2" rx="1" fill="currentColor"/>
                    </svg>
                  </span>
                </div>
              </div>
              <span className="fs-7 fw-bolder text-gray-900">{lang.labels.copilotTitle}</span>
            </div>
            <CopilotActionCard
              action={pendingAction}
              onConfirm={onConfirm}
              onReject={onReject}
            />
          </div>
        </div>
      )}

      {/* Typing indicator (streaming, before first token arrives) */}
      {streaming && !currentToolRunning && messages.length > 0 && messages[messages.length - 1]?.role === 'user' && (
        <div className="d-flex justify-content-start mb-10">
          <div className="d-flex flex-column align-items-start">
            <div className="p-5 rounded bg-light-info text-gray-600">
              <span className="d-inline-flex align-items-center gap-1">
                <span className="spinner-grow spinner-grow-sm" style={{width: '6px', height: '6px', animationDelay: '0s'}}></span>
                <span className="spinner-grow spinner-grow-sm" style={{width: '6px', height: '6px', animationDelay: '0.15s'}}></span>
                <span className="spinner-grow spinner-grow-sm" style={{width: '6px', height: '6px', animationDelay: '0.3s'}}></span>
              </span>
            </div>
          </div>
        </div>
      )}

    </div>
  )
}

export default CopilotMessages;
